import {memo, useCallback, useEffect, useState} from 'react';
import {Box, CircularProgress, Grid, Stack, Typography, useMediaQuery} from '@mui/material';
import Button from '@mui/material/Button';
import {
  DataGrid,
  GridColDef,
  gridPageCountSelector,
  GridPagination, GridToolbar, useGridApiContext,
  useGridApiRef, useGridSelector
} from '@mui/x-data-grid';

import {getCorRuns, getScTranges} from '../../utils/api';
import MhdDatetime from '../atomicComponents/MhdDatetime';
import dayjs from 'dayjs';
import MhdInput from '../atomicComponents/MhdInput';
import {
  corModelFilters,
  runTypeFilters,
  solutionChoices,
  timeOfInterestEventsFilters,
  variableChoices
} from '../../utils/choices';
import MhdMultiSelect from '../atomicComponents/MhdMultiSelect';

import MuiPagination from '@mui/material/Pagination';
import {useTheme} from '@mui/material/styles';
import MhdIconMenu from '../atomicComponents/MhdIconMenu';
import {useDatetimeCarringtonConverter} from "../../hooks/useDatetimeCarringtonConverter";


function Pagination({ page, onPageChange, className }) {
  const apiRef = useGridApiContext();
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);

  return (
    <MuiPagination
      color='primary'
      className={className}
      count={pageCount}
      page={page + 1}
      onChange={(event, newPage) => {
        onPageChange(event, newPage - 1);
      }}
    />
  );
}

function CustomPagination(props) {
  return <GridPagination ActionsComponent={Pagination} {...props} />;
}

const ROW_H = 36;               // compact-ish row height
const HEADER_H = 44;            // column header
const FOOTER_H = 48;            // pagination/footer
const TOOLBAR_H = 48;           // GridToolbar
const MIN_ROWS = 3;


const corCols: GridColDef[] = [
  { field: 'id',
    headerName: 'ID',
    type: 'number',
    width: 40
  },
  { field: 'name',
    headerName: 'Name',
    type: 'string',
    width: 150
  },
  { field: 'start_time',
    headerName: 'Start Time',
    type: 'dateTime',
    width: 120,
    valueGetter: (value) => value && new Date(value),
    format: (value) => value.toLocaleString('UTC')
  },
  { field: 'end_time',
    headerName: 'End Time',
    type: 'dateTime',
    width: 120,
    valueGetter: (value) => value && new Date(value),
    format: (value) => value.toLocaleString('UTC')
  },
  { field: 'run_type',
    headerName: 'Run Type',
    type: 'string',
  },
  { field: 'run_descriptor',
    headerName: 'Run Descriptor',
    type: 'string',
  },
  { field: 'cor_model',
    headerName: 'Coronal Model',
    type: 'string',
  },
  { field: 'surface_data_source',
    headerName: 'Surface Data Source',
    type: 'string',
  },
  { field: 'has_cor',
    headerName: 'Coronal Domain',
    type: 'boolean',
  },
  { field: 'has_hel',
    headerName: 'Heliospheric Domain',
    type: 'boolean',
  },
  { field: 'nr',
    headerName: 'Radial Resolution',
    type: 'number',
  },
  { field: 'nt',
    headerName: 'Theta Resolution',
    type: 'number',
  },
  { field: 'np',
    headerName: 'Phi Resolution',
    type: 'number',
  },
];


const SimDataTable = ({id, data, setter, update, modalViewSetter, modelMetaDataSetter}) => {

  const theme = useTheme();
  const downMd = useMediaQuery(theme.breakpoints.down('md'));
  const apiRef = useGridApiRef();
  const [corId, setCorId] = useState([])
  const [cr0, setCr0] = useState(null)
  const [cr1, setCr1] = useState(null)
  const [cri, setCri] = useState(null)

  const [corData, setCorData] = useState([])
  const [selectedRow, setSelectedRow] = useState([])
  const [submitError, setSubmitError] = useState(false)

  const [filterParams, setFilterParams] = useState({
    time_of_interest: null,
    trange_lower_bound: null,
    trange_upper_bound: null,
    simulation_model: [],
    simulation_type: [],
    radial_domain: [],
    variable_set: []
  })

  const [trangeLowerBoundEventsFilters, setTrangeLowerBoundEventsFilters] = useState([])
  const [trangeUpperBoundEventsFilters, setTrangeUpperBoundEventsFilters] = useState([])

  const [matchingTOI, setMatchingTOI] = useState('')
  const [matchingT0, setMatchingT0] = useState('')
  const [matchingT1, setMatchingT1] = useState('')

  const [isLoading, setIsLoading] = useState(false)
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 })


  useEffect(() => {
    setCorId([data.cor_id])
  }, []);

  const [initialPageSet, setInitialPageSet] = useState(false);

  useEffect(() => {
    if (!initialPageSet && corData.length > 0 && corId[0]) {
      const index = corData.findIndex(row => row.id === corId[0]);
      handleCorCellClick(corId)
      if (index !== -1) {
        const newPage = Math.floor(index / paginationModel.pageSize);
        setPaginationModel({ page: newPage, pageSize: paginationModel.pageSize });

        setTimeout(() => {
          apiRef.current.scrollToIndexes({ rowIndex: index });
        }, 50);

        setInitialPageSet(true);
      }
    }
  }, [corId, corData, initialPageSet]);

  useEffect(() => {
    getScTranges()
      .then(response => {
        setTrangeLowerBoundEventsFilters(response.data.lower_boundaries)
        setTrangeUpperBoundEventsFilters(response.data.upper_boundaries)
      })
      .catch(error => {

      });
  }, [])

  useEffect(() => {
    if (filterParams.time_of_interest !== null) {
      const formattedTime = dayjs(filterParams.time_of_interest).format('YYYY-MM-DD HH:mm:ss');

      const temp = timeOfInterestEventsFilters.find(
        (item) => item.value === formattedTime
      );
      if (temp !== undefined) {
        setMatchingTOI(temp.label)
      } else {
        setMatchingTOI('')
      }
    } else {
      setMatchingTOI('')
    }

  }, [filterParams.time_of_interest]);

  useEffect(() => {
    if (filterParams.trange_lower_bound !== null) {
      const formattedTime = dayjs(filterParams.trange_lower_bound).format('YYYY-MM-DD HH:mm:ss');

      const temp = trangeLowerBoundEventsFilters.find(
        (item) => item.value === formattedTime
      );
      if (temp !== undefined) {
        setMatchingT0(`${temp.label} Lower Data Bound`)
      } else {
        setMatchingT0('')
      }
    } else {
      setMatchingT0('')
    }
  }, [filterParams.trange_lower_bound]);

  useEffect(() => {
      if (filterParams.trange_upper_bound !== null) {
        const formattedTime = dayjs(filterParams.trange_upper_bound).format('YYYY-MM-DD HH:mm:ss');

        const temp = trangeUpperBoundEventsFilters.find(
          (item) => item.value === formattedTime
        );
        if (temp !== undefined) {
          setMatchingT1(`${temp.label} Upper Data Bound`)
        } else {
          setMatchingT1('')
        }
      } else {
        setMatchingT1('')
      }

  }, [filterParams.trange_upper_bound]);

  useEffect(() => {
    const corFilterParams = {
      time_of_interest: filterParams.time_of_interest && dayjs(filterParams.time_of_interest).isValid()
        ? dayjs(filterParams.time_of_interest).format('YYYY-MM-DD HH:mm:ss')
        : '',
      trange_lower_bound: filterParams.trange_lower_bound && dayjs(filterParams.trange_lower_bound).isValid()
        ? dayjs(filterParams.trange_lower_bound).format('YYYY-MM-DD HH:mm:ss')
        : '',
      trange_upper_bound: filterParams.trange_upper_bound && dayjs(filterParams.trange_upper_bound).isValid()
        ? dayjs(filterParams.trange_upper_bound).format('YYYY-MM-DD HH:mm:ss')
        : '',
      simulation_model: filterParams.simulation_model,
      simulation_type: filterParams.simulation_type,
      radial_domain: filterParams.radial_domain,
      variable_set: filterParams.variable_set
    }
    setIsLoading(true)
    getCorRuns(corFilterParams)
      .then(response => {
        setCorData(response.data)
        setIsLoading(false)
      })
      .catch(error => {
        setIsLoading(false)
      });
  }, [filterParams]);

  const handleFilterChange = (fieldName, fieldValue) => {
    setFilterParams(prevData => ({
      ...prevData,
      [fieldName]: fieldValue
    }));
  };

  const { dtToCR, crToDT } = useDatetimeCarringtonConverter(handleFilterChange, () =>{});

  const handleCorCellClick = (params) => {
    setCorId(params);
    const temp = corData.find(
      (item) => item.id === params[0]
    );
    
    if (temp !== undefined) {
      setSelectedRow([temp])
    }
  }

  const handleSubmit = () => {
    if (corId && corId.length !== 0) {
      if (corId[0] === data.cor_id) {
        modalViewSetter(false);
      } else {
        let domain
        if (data.domain === 'cor') {
          domain = selectedRow[0][`has_cor`] ? 'cor' : 'hel'
        } else {
          domain = selectedRow[0][`has_hel`] ? 'hel' : 'cor'
        }
        setSubmitError(false);
        const config = {
          cor_id: corId[0],
          time_of_interest: filterParams.time_of_interest ? dayjs(filterParams.time_of_interest).format('YYYY-MM-DD HH:mm:ss') : '',
          domain: domain
        }

        setter(config);
        update(config)
        modalViewSetter(false)
        modelMetaDataSetter(selectedRow[0] ?? {})
      }
    } else {
      setSubmitError(true);
    }
  };

  const handleClearFilters = () => {
    setFilterParams({
      time_of_interest: null,
      trange_lower_bound: null,
      trange_upper_bound: null,
      simulation_model: [],
      simulation_type: [],
      radial_domain: [],
      variable_set: []
    })
    setCr0(null)
    setCr1(null)
    setCri(null)
  }

  return (
    <>
      <Stack spacing={1} sx={{ width: '100%', height: '100%' }}>
        <Typography
          fontSize={{xs: 'large', sm: 'large', md: 'x-large'}}
          textAlign="right"
        >
          SIMULATION DATABASE
        </Typography>
        {!data.cor_id && !corId.length && (
          <Typography
            fontSize={{xs: 'x-small', sm: 'small'}}
            color="error"
            textAlign="center"
            // fontStyle={'italic'}
            mb={-1}
          >
            <i>To get started, select a simulation from the MAS Run Database</i> ⬇️
          </Typography>
        )}

          <Box
            sx={{
              width: '100%',
              minWidth: 0,
              minHeight: '33em',
              maxHeight: '66vh',
              // flex: '1 0 auto',
            }}
          >
            <DataGrid
              rows={corData}
              columns={corCols}
              apiRef={apiRef}
              rowSelectionModel={corId}
              onRowSelectionModelChange={handleCorCellClick}
              paginationModel={paginationModel}
              onPaginationModelChange={(model) => setPaginationModel(model)}
              autoHeight={false}
              pageSizeOptions={[5, 10, 20, 50, 100]}
              disableMultipleRowSelection
              checkboxSelection
              slots={{ toolbar: GridToolbar, pagination: CustomPagination }}
              initialState={{
                columns: { columnVisibilityModel: { id:false, } },
                density: 'compact'
              }}
              sx={{
                '& .MuiDataGrid-footerContainer, & .MuiTablePagination-root, & .MuiTablePagination-toolbar, & .MuiTablePagination-actions': {
                  backgroundColor: (t) => t.palette.base.rothko6,
                },
              }}
            />
          </Box>

        <Stack direction={{xs: 'column', md: 'row'}} spacing={1}>
          <Box
            width={'100%'}
            sx={{
              border: '1px solid #c5c5c5',
              p: 1,
              borderRadius: '8px',
              bgcolor: theme.palette.base.rothko5,
            }}
          >
            <Grid container spacing={1}>
              <Grid item xs={1} mt={'12px'} mb={0}>
                <Box
                  display='flex'
                  alignItems='start'
                  justifyContent='center'
                  height='100%'
                >
                  <MhdIconMenu
                    label={'Spacecraft Time Ranges'}
                    choices={trangeLowerBoundEventsFilters}
                    onChange={useCallback((v) => dtToCR('trange_lower_bound', v, setCr0), [])}
                  />
                </Box>
              </Grid>

              <Grid item xs={7}>
                <MhdDatetime
                  label={'Time Range (Start)'}
                  value={filterParams.trange_lower_bound}
                  onChange={useCallback((v) => dtToCR('trange_lower_bound', v, setCr0), [])}
                />
                <Typography
                  variant={'caption'}
                  color={'primary'}
                  ml={'2px'}
                  fontStyle={'italic'}
                >
                  {matchingT0}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <MhdInput
                  label={'CR Range (Start)'}
                  value={cr0}
                  onChange={useCallback((v) => crToDT('trange_lower_bound', v, setCr0), [])}
                  units={'CR'}
                  clear={true}
                />
              </Grid>
              <Grid item xs={1} mt={'12px'} mb={0}>
                <Box
                  display='flex'
                  alignItems='start'
                  justifyContent='center'
                  height='100%'
                >
                 <MhdIconMenu
                    label={'Spacecraft Time Ranges'}
                    choices={trangeUpperBoundEventsFilters}
                    onChange={useCallback((v) => dtToCR('trange_upper_bound', v, setCr1), [])}
                  />
                </Box>
              </Grid>
              <Grid item xs={7}>
                <MhdDatetime
                  label={'Time Range (End)'}
                  value={filterParams.trange_upper_bound}
                  onChange={useCallback((v) => dtToCR('trange_upper_bound', v, setCr1), [])}
                />
                <Typography
                  variant={'caption'}
                  color={'primary'}
                  ml={'2px'}
                  fontStyle={'italic'}
                >
                  {matchingT1}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <MhdInput
                  label={'CR Range (End)'}
                  value={cr1}
                  onChange={useCallback((v) => crToDT('trange_upper_bound', v, setCr1), [])}
                  units={'CR'}
                  clear={true}
                />
              </Grid>
              <Grid item xs={1} mt={'12px'} mb={0}>
                <Box
                  display='flex'
                  alignItems='start'
                  justifyContent='center'
                  height='100%'
                >
                <MhdIconMenu
                  label={'Key Events'}
                  choices={timeOfInterestEventsFilters}
                  onChange={useCallback((v) => dtToCR('time_of_interest', v, setCri), [])}
                />
                </Box>
              </Grid>
              <Grid item xs={7}>
                <MhdDatetime
                  label={'Time Of Interest'}
                  value={filterParams.time_of_interest}
                  onChange={useCallback((v) => dtToCR('time_of_interest', v, setCri), [])}
                />
                <Typography
                  variant={'caption'}
                  color={'primary'}
                  ml={'2px'}
                  fontStyle={'italic'}
                >
                  {matchingTOI}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <MhdInput
                  label={'CR of Interest'}
                  value={cri}
                  onChange={useCallback((v) => crToDT('time_of_interest', v, setCri), [])}
                  units={'CR'}
                  clear={true}
                />
              </Grid>
            </Grid>
          </Box>
          <Box
            width={'100%'}
            sx={{
              border: '1px solid #c5c5c5',
              p: 1,
              borderRadius: '8px',
              bgcolor: theme.palette.base.rothko5,
            }}
          >
            <Grid container spacing={1}>
              <Grid item xs={6} pb={2}>
                <MhdMultiSelect
                  label='Models'
                  choices={corModelFilters}
                  value={filterParams.simulation_model}
                  onChange={useCallback((v) => handleFilterChange('simulation_model', v), [])}
                />
              </Grid>
              <Grid item xs={6} pb={2}>
                <MhdMultiSelect
                  label='Types'
                  choices={runTypeFilters}
                  value={filterParams.simulation_type}
                  onChange={useCallback((v) => handleFilterChange('simulation_type', v), [])}
                />
              </Grid>
              <Grid item xs={6} pb={2}>
                <MhdMultiSelect
                  label='Domains'
                  choices={solutionChoices}
                  value={filterParams.radial_domain}
                  onChange={useCallback((v) => handleFilterChange('radial_domain', v), [])}
                />
              </Grid>
              <Grid item xs={6} pb={2}>
                <MhdMultiSelect
                  label='Variables'
                  choices={variableChoices}
                  value={filterParams.variable_set}
                  onChange={useCallback((v) => handleFilterChange('variable_set', v), [])}
                />
              </Grid>
              <Grid item xs={12} mt={1}>
                <Button fullWidth onClick={handleClearFilters} variant={'contained'} color={'error'}>
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Stack>

        <Stack direction={'column'} alignItems={'center'}>
          {isLoading && (
            <Box
              sx={{
                alignSelf: 'stretch',     // override parent's alignItems
                width: '100%',
                minWidth: 0,              // allow shrinking inside flex rows
                display: 'flex',          // center contents while Box spans full width
                flexDirection: 'column',
                alignItems: 'center',
                zIndex: 5,
              }}
            >
              <CircularProgress size={64} thickness={2} />
              <Typography fontSize={14} color="primary">FETCHING DATABASE...</Typography>
            </Box>
          )}
          <Stack direction={'row'} spacing={1} justifyContent={'center'} mb={1}>
            <Button
              onClick={handleSubmit}
              variant={'contained'}
              color={'secondary'}
              disabled={isLoading}
            >
                Submit
            </Button>
            <Button
              onClick={(e) => modalViewSetter(false)}
              variant={'contained'}
              color={'secondary'}
            >
              Close
            </Button>
          </Stack>
          {(submitError === true) &&
            <Typography variant='caption' color='error' marginTop='5px'>
              Please select a run
            </Typography>
          }
          </Stack>
        </Stack>
    </>
  )
}

export default memo(SimDataTable);